import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Tooltip, Tree } from 'antd';
import type { GetRef, TreeDataNode, TreeProps } from 'antd';
import SearchInput from 'components/common/SearchInput/SearchInput';
import {
  SwitcherIcon,
  TreeErrorState,
  TreeLoadingState,
} from 'components/common/TreeStates/TreeStates';
import {
  COPY_TOOL_LOAD_ERROR_MESSAGE,
  COPY_TOOL_NO_PADDOCKS_MESSAGE,
} from 'constants/messageConstants';
import {
  COPY_TOOL_SEARCH_ARIA_LABEL,
  FARM_TREE_LOADING_MESSAGE,
  TREE_SEARCH_NO_MATCHES_MESSAGE,
  TREE_SEARCH_PLACEHOLDER,
} from 'constants/uiConstants';
import 'styles/farmTreeStyle.css';
import type { TreeGroupBy } from 'utils/FarmUtils';
import MergeClassNames from 'utils/MergeClassNames';

import {
  CopyTreeIndex,
  CopyTreeNode,
  getInputKeysById,
} from './buildCopyTreeData';
import { toTreeCheckedKeys, withLinkedCopies } from './copySelection';
import {
  applyShownCheck,
  disableEmptyGroups,
  fitAddAsNewDocuments,
  getBlockedAddAsNewDocuments,
  getShownCheckedKeys,
  indexShownNodes,
  PaddockOwners,
  resolveAddAsNewOwnership,
  ShownCheckContext,
} from './copyTreeChecks';
import CopyTreeGroupByToggle from './CopyTreeGroupByToggle';

interface CopySelectionTreeProps {
  label: string;
  hint: string;
  treeData: TreeDataNode[];
  /** The panel's index, mode and search, shared with the view that built the tree. */
  checkContext: ShownCheckContext;
  checkedKeys: string[];
  expandedKeys: string[];
  isLoading: boolean;
  isError: boolean;
  onCheckedKeysChange: (keys: string[]) => void;
  onExpandedKeysChange: (keys: string[]) => void;
  scrollToKey?: string | null;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  /** A search is applied, so an empty tree means no matches. */
  isSearching: boolean;
  groupBy: TreeGroupBy;
  onGroupByChange: (groupBy: TreeGroupBy) => void;
}

const NO_BLOCKED_DOCUMENTS: ReadonlySet<string> = new Set();

/**
 * Marks every other visible row, counting in display order through expanded
 * nodes only. Done on the data, not with :nth-child, as the virtual list
 * renders a moving slice of rows and the stripes would shift while scrolling.
 */
const stripeVisibleRows = (
  treeData: TreeDataNode[],
  expandedKeys: string[]
): TreeDataNode[] => {
  const expanded = new Set(expandedKeys);
  let row = 0;
  const stripe = (nodes: TreeDataNode[]): TreeDataNode[] =>
    nodes.map((node) => {
      const isStriped = row % 2 === 1;
      row += 1;
      const children =
        node.children && expanded.has(String(node.key))
          ? stripe(node.children)
          : node.children;
      return {
        ...node,
        className: isStriped
          ? MergeClassNames(node.className, 'copy-tree-row-striped')
          : node.className,
        children,
      };
    });
  return stripe(treeData);
};

// Module-level, so the virtual tree's rows aren't re-rendered by a new prop each render.
const renderSwitcherIcon = (props: { expanded?: boolean }) => (
  <SwitcherIcon expanded={props.expanded ?? false} />
);

// The row's title, with a greyed-out tick's reason on hover.
const renderTitle = (node: CopyTreeNode) => {
  const title =
    typeof node.title === 'function' ? node.title(node) : node.title;
  if (!node.disableCheckbox || !node.disabledReason) return title;
  return (
    <Tooltip title={node.disabledReason}>
      <span className="flex min-w-0 flex-1 items-center">{title}</span>
    </Tooltip>
  );
};

/** One side of the copy tool: a labelled, tickable farm → paddock → plan tree. */
const CopySelectionTree: React.FC<CopySelectionTreeProps> = ({
  label,
  hint,
  treeData,
  checkContext,
  checkedKeys,
  expandedKeys,
  isLoading,
  isError,
  onCheckedKeysChange,
  onExpandedKeysChange,
  scrollToKey = null,
  searchQuery,
  onSearchQueryChange,
  isSearching,
  groupBy,
  onGroupByChange,
}) => {
  const { index, allowMultiple } = checkContext;
  // Add as new: which document ticked each paddock; dropped when the panel's tree changes (year, mode).
  const [recordedOwners, setRecordedOwners] = useState<{
    index: CopyTreeIndex;
    owners: PaddockOwners;
  } | null>(null);
  const owners = useMemo(
    () => (recordedOwners?.index === index ? recordedOwners.owners : {}),
    [recordedOwners, index]
  );

  // Fixed-height trees are virtualised by antd, so large organisations render only the rows in view.
  const bodyRef = useRef<HTMLDivElement>(null);
  const [bodyHeight, setBodyHeight] = useState(0);
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return undefined;
    setBodyHeight(body.clientHeight);
    const observer = new ResizeObserver(() => setBodyHeight(body.clientHeight));
    observer.observe(body);
    return () => observer.disconnect();
  }, []);

  // Add as new: who holds each ticked paddock, read by the greying, the ticks shown and a tick.
  const ownership = useMemo(
    () => resolveAddAsNewOwnership(treeData, checkedKeys, owners, checkContext),
    [treeData, checkedKeys, owners, checkContext]
  );
  // The previous set is kept while the same documents stay greyed out, so a tick doesn't rebuild the tree.
  const blockedDocKeysRef = useRef(NO_BLOCKED_DOCUMENTS);
  const blockedDocKeys = useMemo(() => {
    const next = getBlockedAddAsNewDocuments(ownership);
    const previous = blockedDocKeysRef.current;
    return next.length === previous.size &&
      next.every((key) => previous.has(key))
      ? previous
      : new Set(next);
  }, [ownership]);
  useEffect(() => {
    blockedDocKeysRef.current = blockedDocKeys;
  }, [blockedDocKeys]);
  // The tree as drawn: in Add as new one document per ticked paddock, then dead group ticks greyed out.
  const shownTreeData = useMemo(
    () =>
      disableEmptyGroups(
        fitAddAsNewDocuments(treeData, blockedDocKeys),
        checkContext
      ),
    [treeData, blockedDocKeys, checkContext]
  );
  const shownNodes = useMemo(
    () => indexShownNodes(shownTreeData),
    [shownTreeData]
  );
  const stripedTreeData = useMemo(
    () => stripeVisibleRows(shownTreeData, expandedKeys),
    [shownTreeData, expandedKeys]
  );

  // A linked document's shown row: Crop view keys it under its first paddock in tree order.
  const shownScrollKey = useMemo(() => {
    if (!scrollToKey) return null;
    const info = index.get(scrollToKey);
    const keys =
      info?.kind === 'input'
        ? (getInputKeysById(index).get(info.input.id) ?? [scrollToKey])
        : [scrollToKey];
    return keys.find((key) => shownNodes.has(key)) ?? null;
  }, [scrollToKey, index, shownNodes]);

  // scrollTo, not scrollIntoView: the virtual tree only renders the rows in view.
  const treeRef = useRef<GetRef<typeof Tree>>(null);
  // The views already scrolled to the row, so each view scrolls to it once.
  const scrolledRef = useRef<{ key: string | null; views: Set<TreeGroupBy> }>({
    key: null,
    views: new Set(),
  });
  useEffect(() => {
    // Waits until the row is shown, e.g. after a search or in a view not yet scrolled.
    if (!scrollToKey || !shownScrollKey || bodyHeight === 0) return undefined;
    if (scrolledRef.current.key !== scrollToKey) {
      scrolledRef.current = { key: scrollToKey, views: new Set() };
    }
    const { views } = scrolledRef.current;
    if (views.has(groupBy)) return undefined;
    // A frame later, so the virtual list has laid out the new rows.
    const frame = requestAnimationFrame(() => {
      treeRef.current?.scrollTo({ key: shownScrollKey, align: 'auto' });
      views.add(groupBy);
    });
    return () => cancelAnimationFrame(frame);
  }, [scrollToKey, shownScrollKey, bodyHeight, groupBy]);

  const treeCheckedKeys = useMemo(
    () =>
      getShownCheckedKeys(
        shownTreeData,
        toTreeCheckedKeys(
          // Display only: a single-document panel shows a linked plan ticked under every paddock.
          allowMultiple ? checkedKeys : withLinkedCopies(checkedKeys, index),
          index
        ),
        checkContext,
        ownership
      ),
    [shownTreeData, allowMultiple, checkedKeys, index, checkContext, ownership]
  );

  const handleCheck: TreeProps['onCheck'] = (_, info) => {
    const node = shownNodes.get(String(info.node.key));
    if (!node) return;
    const next = applyShownCheck(
      checkedKeys,
      owners,
      node,
      info.checked,
      checkContext,
      ownership
    );
    if (checkContext.paddocksOnly) {
      setRecordedOwners({ index, owners: next.owners });
    }
    onCheckedKeysChange(next.keys);
  };

  const renderBody = () => {
    if (isLoading)
      return <TreeLoadingState message={FARM_TREE_LOADING_MESSAGE} />;
    if (isError)
      return <TreeErrorState message={COPY_TOOL_LOAD_ERROR_MESSAGE} />;
    if (treeData.length === 0) {
      return (
        <p className="px-4 py-3 text-sm text-gray-muted">
          {isSearching
            ? TREE_SEARCH_NO_MATCHES_MESSAGE
            : COPY_TOOL_NO_PADDOCKS_MESSAGE}
        </p>
      );
    }
    return (
      <Tree
        ref={treeRef}
        className="custom-tree copy-tool-tree select-none"
        treeData={stripedTreeData}
        checkable
        checkStrictly
        selectable={false}
        blockNode
        checkedKeys={treeCheckedKeys}
        onCheck={handleCheck}
        expandedKeys={expandedKeys}
        height={bodyHeight > 0 ? bodyHeight : undefined}
        onExpand={(keys) => onExpandedKeysChange(keys.map(String))}
        switcherIcon={renderSwitcherIcon}
        titleRender={renderTitle}
      />
    );
  };

  return (
    <section
      aria-label={label}
      className="flex min-h-0 min-w-0 flex-1 flex-col rounded-lg border border-gray-medium"
    >
      <header className="flex items-baseline gap-2 border-b border-gray-medium px-3 py-1.5">
        <h3 className="flex-shrink-0 text-sm font-semibold text-charcoal">
          {label}
        </h3>
        <div>
          <span className="truncate text-xs text-gray-muted" title={hint}>
            {hint}
          </span>
        </div>
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <SearchInput
            value={searchQuery}
            onChange={onSearchQueryChange}
            placeholder={TREE_SEARCH_PLACEHOLDER}
            ariaLabel={COPY_TOOL_SEARCH_ARIA_LABEL(label)}
            autoFocus={false}
            size="small"
          />
          <CopyTreeGroupByToggle
            side={label}
            value={groupBy}
            onChange={onGroupByChange}
          />
        </div>
      </header>
      <div
        ref={bodyRef}
        className={MergeClassNames(
          'h-[40vh] min-h-0 md:h-auto md:flex-1',
          bodyHeight > 0
            ? 'overflow-hidden'
            : 'overflow-y-auto overflow-x-hidden'
        )}
      >
        {renderBody()}
      </div>
    </section>
  );
};

export default CopySelectionTree;
